
import { Component, Vue, Prop } from "vue-property-decorator";
import { API_ROUTES } from "@/utils/API";
import { SMTeammates, SMUser } from "@/store";

import { TTeammates_Table } from "@/models/company_teammates";

import { ASK_POPUP_TYPE, WARNING_POPUP_TYPE } from "@/constants/popupTypes";

import { SUCCESS_NOTIFICATION_TYPE, ERROR_NOTIFICATION_TYPE } from "@/constants/notification";

const VALUE_NAME = "name";
const VALUE_EMAIL = "email";
const VALUE_PERMISSION = "permission";

const REMOVE_TEAMMATE_ROUTE = "RemoveTeammate";

@Component
export default class TeammatesPendingInvitesTable extends Vue {
  @Prop({ type: Array, default: () => [] }) readonly teammates!: TTeammates_Table[];
  @Prop({ type: Boolean, default: false }) readonly loading!: boolean;

  assignAdminPopup = false;

  removeTeammatePopup = false;

  askPopupType = ASK_POPUP_TYPE;

  warningPopupType = WARNING_POPUP_TYPE;

  userId = 0;

  isCurrentUser(id: number) {
    return SMUser.user?.ID === id;
  }

  get complianceAdminName() {
    const admin = this.teammates.find((teammate) => teammate.complianceAdmin);

    if (!admin) return "";

    return admin.name;
  }

  popup = {
    toName: "",
  };

  openAssignAdminPopup(id: number, name: string) {
    this.userId = id;

    this.popup.toName = name;

    this.assignAdminPopup = true;
  }

  closeAssignAdminPopup() {
    this.assignAdminPopup = false;
  }

  async makeCoplianceAdmin() {
    if (!this.userId) return;

    try {
      await this.axios.get(API_ROUTES.COMPANY_TEAMMATES_COMPLIANCE_ADMIN(this.userId));

      this.$root.$emit("show-notification", {
        type: SUCCESS_NOTIFICATION_TYPE,
        title: this.$tc("notification.newComplianceAdminAassigned"),
        message: this.$tc("notification.complianceDutiesTransferred"),
      });

      this.$emit("update");
    } catch (err) {
      this.$root.$emit("show-notification", {
        type: ERROR_NOTIFICATION_TYPE,
        title: this.$tc("notification.error.title"),
        message: this.$tc("notification.error.message"),
      });
    } finally {
      this.closeAssignAdminPopup();
    }
  }

  $refs!: {
    label: HTMLElement;
  };

  async changePermission(id: number, member: string) {
    try {
      await this.axios.get(API_ROUTES.COMPANY_TEAMMATES_PERMISSION(id, member));

      this.$refs.label.click();
    } finally {
      this.$emit("update");
    }
  }

  openRemoveTeammatePopup(id: number, name: string) {
    this.userId = id;

    this.popup.toName = name;

    this.removeTeammatePopup = true;
  }

  closeRemoveTeammatePopup() {
    this.removeTeammatePopup = false;
  }

  get deleteLoading() {
    return SMTeammates.deleteLoading;
  }

  async removeTeammate() {
    await SMTeammates.getTeammatesDeleteStatus(this.userId);

    const { is_compliance_admin, is_has_cases } = SMTeammates.status;

    if (is_compliance_admin || is_has_cases)
      return await this.$router.push({
        name: REMOVE_TEAMMATE_ROUTE,
        params: { teammateId: String(this.userId) },
      });

    const params = {
      new_compliance_admin_id: 0,
      case_redistribution: [],
    };

    await SMTeammates.deleteTeammate({ userId: this.userId, params });

    this.$root.$emit("show-notification", {
      type: SUCCESS_NOTIFICATION_TYPE,
      title: this.$tc("notification.teammateIsRemoved"),
    });

    this.closeRemoveTeammatePopup();

    await SMTeammates.getTeammates();
  }

  get attributes() {
    const { headers, teammates, loading, labels } = this;
    return {
      headers,
      items: teammates,
      loading,
      labels,
    };
  }

  get labels() {
    return {
      allLabel: this.$t("table.allLabel"),
      placeholderLabel: this.$t("table.placeholderLabel"),
    };
  }

  headers = [
    {
      text: this.$t("table.headers.name"),
      value: VALUE_NAME,
      filterable: true,
      slot: true,
    },
    {
      text: this.$t("table.headers.email"),
      value: VALUE_EMAIL,
      filterable: true,
    },
    {
      text: this.$t("table.headers.permission"),
      value: VALUE_PERMISSION,
      filterable: true,
      hint: this.$t("table.hint.permission"),
      slot: true,
    },
  ];
}
